import React, { useEffect, useState } from "react";
import { FaSmile, FaMeh, FaFrown, FaSpinner } from "react-icons/fa";
import { useNotas } from "../../context/NotasContext";
import { useAuth } from "../../context/AuthContext";

const NotasListStudentView = ({ notas }) => {
  const { user } = useAuth();
  const { verificarAccesoNotas } = useNotas();
  
  const [tieneAcceso, setTieneAcceso] = useState(null);
  const [cargando, setCargando] = useState(true);

  // Verificar acceso del estudiante al montar el componente
  useEffect(() => {
    const verificarAccesoEstudiante = async () => {
      if (!user?.id) return;
      
      setCargando(true);
      try {
        const acceso = await verificarAccesoNotas(user.id);
        setTieneAcceso(acceso);
      } catch (error) {
        setTieneAcceso(false);
      } finally {
        setCargando(false);
      }
    };

    verificarAccesoEstudiante();
  }, [user?.id, verificarAccesoNotas]);

  // Mostrar loader mientras se verifica el acceso
  if (cargando || tieneAcceso === null) {
    return (
      <div className="bg-white p-8 rounded-2xl shadow-md mt-10 flex flex-col items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-blue-600 mb-4" />
        <p className="text-gray-600">Verificando acceso a notas...</p>
      </div>
    );
  }

  // Mostrar mensaje si el estudiante no tiene acceso
  if (tieneAcceso === false) {
    return (
      <div className="bg-white p-8 rounded-2xl shadow-md mt-10 flex flex-col items-center text-center">
        <img
          src="/logo.png"
          alt="Logo Colegio"
          className="w-64 mb-4 drop-shadow-xl"
        />
        <p className="text-xl font-semibold max-w-md text-gray-700">
          Las notas no están disponibles actualmente. Por favor, comunícate
          con la institución si crees que esto es un error.
        </p>
      </div>
    );
  }

  // Agrupar calificaciones por asignatura
  const groupedNotas = notas.reduce((acc, nota) => {
    if (!acc[nota.asignatura_id]) {
      acc[nota.asignatura_id] = {
        asignatura_nombre: nota.asignatura_nombre,
        promedio_final: nota.promedio_final,
        periodos: Array(5).fill(null),
      };
    }
    acc[nota.asignatura_id].periodos[nota.periodo_id - 1] = nota;
    return acc;
  }, {});

  const calcularPromedioFinal = (periodos) => {
    const notasValidas = periodos
      .slice(0, 4)
      .filter(p => p && p.calificacion && !isNaN(p.calificacion))
      .map(p => parseFloat(p.calificacion));
    
    if (notasValidas.length === 0) return 0;
    
    const promedio = notasValidas.reduce((sum, nota) => sum + nota, 0) / notasValidas.length;
    return promedio.toFixed(2);
  };

  const renderCalificacion = (nota, esPeriodoFinal = false, periodos = null) => {
    if (esPeriodoFinal && periodos) {
      const promedio = calcularPromedioFinal(periodos);
      return (
        <div className="flex justify-center font-bold text-blue-600">
          {promedio}
        </div>
      );
    }

    if (!nota) {
      return "-";
    }

    if (nota.simbolo_carita) {
      switch (nota.simbolo_carita) {
        case "feliz":
          return (
            <div className="flex justify-center">
              <FaSmile className="text-green text-2xl" />
            </div>
          );
        case "contento":
          return (
            <div className="flex justify-center">
              <FaSmile className="text-main-blue text-2xl" />
            </div>
          );
        case "intermedio":
          return (
            <div className="flex justify-center">
              <FaMeh className="text-yellow-500 text-2xl" />
            </div>
          );
        case "triste":
          return (
            <div className="flex justify-center">
              <FaFrown className="text-red-500 text-2xl" />
            </div>
          );
        default:
          return (
            <div className="flex justify-center">
              {nota.calificacion || "-"}
            </div>
          );
      }
    }

    return (
      <div className="flex justify-center">{nota.calificacion || "-"}</div>
    );
  };

  const calcularPromedioGeneral = () => {
    const asignaturas = Object.values(groupedNotas);
    const promedios = asignaturas
      .map(asig => calcularPromedioFinal(asig.periodos))
      .filter(prom => prom > 0)
      .map(prom => parseFloat(prom));
    
    if (promedios.length === 0) return 0;
    
    const promedioGeneral = promedios.reduce((sum, prom) => sum + prom, 0) / promedios.length;
    return promedioGeneral.toFixed(2);
  };

  return (
    <div className="container mx-auto px-4">
      <div className="bg-white p-6 rounded-2xl shadow-md mt-6">
        <h3 className="text-xl font-semibold mb-4">Mis Notas</h3>
        {Object.keys(groupedNotas).length === 0 ? (
          <p>No hay notas registradas.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full table-auto border-collapse border border-gray-200">
                <thead>
                  <tr className="bg-gray">
                    <th className="border border-gray px-4 py-2">Asignatura</th>
                    <th className="border border-gray px-4 py-2">Periodo 1</th>
                    <th className="border border-gray px-4 py-2">Periodo 2</th>
                    <th className="border border-gray px-4 py-2">Periodo 3</th>
                    <th className="border border-gray px-4 py-2">Periodo 4</th>
                    <th className="border border-gray px-4 py-2 bg-blue-100">Promedio Final</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(groupedNotas).map((nota) => (
                    <tr key={nota.asignatura_nombre}>
                      <td className="border border-gray px-4 py-2">
                        {nota.asignatura_nombre}
                      </td>
                      {nota.periodos.slice(0, 4).map((periodo, index) => (
                        <td
                          key={index}
                          className="border border-gray px-4 py-2 text-center"
                        >
                          {renderCalificacion(periodo)}
                        </td>
                      ))}
                      <td className="border border-gray px-4 py-2 text-center font-bold bg-blue-50">
                        {renderCalificacion(null, true, nota.periodos)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-500">
              <h4 className="text-lg font-semibold text-blue-700 mb-2">
                Resumen Académico
              </h4>
              <p className="text-2xl font-bold text-blue-800">
                Promedio General: <span className="text-3xl">{calcularPromedioGeneral()}</span>
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Basado en el promedio de todas las asignaturas
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NotasListStudentView;